import { Button } from '@/shared/components/atoms/ui/button'
import { Input } from '@/shared/components/atoms/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/atoms/ui/select'

export interface FilterOption {
	label: string
	value: string
}

export interface FilterDefinition {
	key: string
	label: string
	type: 'text' | 'select' | 'date'
	options?: FilterOption[]
}

interface FilterBarProps {
	filters: FilterDefinition[]
	values: Record<string, string>
	onChange: (values: Record<string, string>) => void
	onApply: () => void
	onClear: () => void
}

const ALL_VALUE = '__all__'

export function FilterBar({ filters, values, onChange, onApply, onClear }: FilterBarProps) {
	const updateFilter = (key: string, value: string) => {
		onChange({ ...values, [key]: value })
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') onApply()
	}

	const hasActiveFilters = Object.values(values).some((v) => v && v.trim() !== '')

	return (
		<div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-muted rounded-lg border border-border">
			{filters.map((filter) => (
				<FilterControl
					key={filter.key}
					filter={filter}
					value={values[filter.key] ?? ''}
					onUpdate={(v) => updateFilter(filter.key, v)}
					onKeyDown={handleKeyDown}
				/>
			))}
			<div className="flex gap-2">
				<Button size="sm" onClick={onApply}>
					Filter
				</Button>
				{hasActiveFilters && (
					<Button size="sm" variant="outline" onClick={onClear}>
						Clear
					</Button>
				)}
			</div>
		</div>
	)
}

interface FilterControlProps {
	filter: FilterDefinition
	value: string
	onUpdate: (value: string) => void
	onKeyDown: (e: React.KeyboardEvent) => void
}

function FilterControl({ filter, value, onUpdate, onKeyDown }: FilterControlProps) {
	const id = `filter-${filter.key}`
	return (
		<div className="flex flex-col gap-1">
			<label htmlFor={id} className="text-xs font-medium text-muted-foreground">
				{filter.label}
			</label>
			{filter.type === 'text' && (
				<Input
					id={id}
					placeholder={filter.label}
					value={value}
					onChange={(e) => onUpdate(e.target.value)}
					onKeyDown={onKeyDown}
					className="h-8 w-40 text-sm"
				/>
			)}
			{filter.type === 'select' && (
				<Select value={value ? value : ALL_VALUE} onValueChange={(v) => onUpdate(v === ALL_VALUE ? '' : v)}>
					<SelectTrigger id={id} className="h-8 w-40 text-sm">
						<SelectValue placeholder="All" />
					</SelectTrigger>
					<SelectContent>
						{/* Radix forbids empty-string SelectItem values, but consumers may pass
						    { value: '', label: '...' } (the legacy "All" sentinel). Map any
						    empty-string option value to ALL_VALUE for rendering, and map back
						    on selection. Skip the implicit "All" entry if the consumer already
						    provides an empty-value option. */}
						{!filter.options?.some((opt) => opt.value === '') && <SelectItem value={ALL_VALUE}>All</SelectItem>}
						{filter.options?.map((opt) => (
							<SelectItem key={opt.value || ALL_VALUE} value={opt.value || ALL_VALUE}>
								{opt.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}
			{filter.type === 'date' && (
				<Input
					id={id}
					type="date"
					value={value}
					onChange={(e) => onUpdate(e.target.value)}
					onKeyDown={onKeyDown}
					className="h-8 w-40 text-sm"
				/>
			)}
		</div>
	)
}
